import { fmt, fmtDate } from "../components/ui-kit";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { StatusBadge } from "../components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Search, Plus, Video, Clock, FileEdit, MessageSquare, Copy } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { DobInput } from "../components/DobInput";
import { toast } from "sonner";

export default function Sessions() {
  const navigate = useNavigate();
  const { activeMembership } = useAuth();
  const [rows, setRows] = useState([]);
  const roomUrl = activeMembership?.room_slug ? `${window.location.origin}/${activeMembership.company_slug}/${activeMembership.room_slug}` : null;
  const reminder = `Hi — this is ${activeMembership?.company_name}. Here is the link for your telehealth visit. Open it on your phone a few minutes before we start: ${roomUrl}`;
  const [types, setTypes] = useState({});
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ client_first: "", client_last: "", dob: "", document_type: "" });

  const load = () => api.get("/sessions", { params: q ? { q } : {} }).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);
  useEffect(() => { api.get("/sessions/doc-types").then((r) => setTypes(r.data)); }, []);

  const create = async () => {
    try {
      const { data } = await api.post("/sessions", f);
      setOpen(false);
      navigate(`/app/sessions/${data.id}/room`);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Clinical Rooms</h1>
          <p className="mt-1 text-muted-foreground">Your personal room for 1-to-1 visits, plus group rooms where everyone joins from one shared link.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="h-11 rounded-full" data-testid="new-session-button"><Plus className="mr-2 h-4 w-4" /> New session</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Start a telehealth session</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>First name</Label><Input data-testid="session-first" value={f.client_first} onChange={(e) => setF({ ...f, client_first: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Last name</Label><Input data-testid="session-last" value={f.client_last} onChange={(e) => setF({ ...f, client_last: e.target.value })} /></div>
              </div>
              <div className="space-y-1.5"><Label>Date of birth</Label><DobInput testid="session-dob" value={f.dob} onChange={(v) => setF({ ...f, dob: v })} className="h-11" /></div>
              <div className="space-y-1.5">
                <Label>Document type</Label>
                <Select value={f.document_type} onValueChange={(v) => setF({ ...f, document_type: v })}>
                  <SelectTrigger data-testid="session-doctype" className="h-11"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>{Object.keys(types).map((t) => <SelectItem key={t} value={t} data-testid={`doctype-${t.replace(/[^a-z]+/gi, "-").toLowerCase()}`}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button onClick={create} disabled={!f.client_last || !f.dob || !f.document_type} data-testid="create-session-submit"><Video className="mr-2 h-4 w-4" /> Start session</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <GroupRooms />
      {roomUrl && <WaitingList types={types} />}
      {roomUrl && (
        <div className="flex flex-col gap-3 rounded-[2rem] border border-border bg-card p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="room-reminder-card">
          <div className="min-w-0"><p className="font-head font-bold">My session room</p><p className="truncate font-mono text-xs text-muted-foreground">{roomUrl}</p></div>
          <div className="flex shrink-0 gap-2">
            <Button asChild className="rounded-full"><a href={`sms:?&body=${encodeURIComponent(reminder)}`} data-testid="text-room-link"><MessageSquare className="mr-2 h-4 w-4" /> Text client my link</a></Button>
            <Button variant="outline" className="rounded-full" onClick={() => { navigator.clipboard.writeText(reminder); toast.success("Reminder copied"); }} data-testid="copy-reminder"><Copy className="mr-2 h-4 w-4" /> Copy reminder</Button>
          </div>
        </div>
      )}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid="session-search" placeholder="Search by client name or date of birth (MM/DD/YYYY)" value={q} onChange={(e) => setQ(e.target.value)} className="h-11 pl-9" />
      </div>

      <div className="space-y-3">
        {rows.length === 0 && <p className="text-sm text-muted-foreground">No sessions match.</p>}
        {rows.map((s) => (
          <button key={s.id} data-testid={`session-row-${s.id}`} onClick={() => navigate(s.status === "in_session" ? `/app/sessions/${s.id}/room` : `/app/sessions/${s.id}`)}
            className="flex w-full flex-col gap-2 rounded-2xl border border-border bg-card p-4 text-left transition-colors hover:border-primary/40 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                {s.status === "in_session" ? <Video className="h-4 w-4 text-primary" /> : <FileEdit className="h-4 w-4 text-primary" />}
                <span className="font-head font-bold">{s.client_name}</span>
                <span className="text-xs text-muted-foreground">DOB {s.dob}</span>
              </div>
              <p className="mt-0.5 text-sm text-muted-foreground">{s.document_type} · {s.clinician}</p>
              {s.expires_at && <p className="mt-1 flex items-center gap-1 text-xs text-accent"><Clock className="h-3 w-3" /> Auto-deletes {fmtDate(s.expires_at)}</p>}
            </div>
            <StatusBadge status={s.status} />
          </button>
        ))}
      </div>
    </div>
  );
}

function WaitingList({ types }) {
  const [rows, setRows] = React.useState([]);
  const navigate = useNavigate();
  const load = () => api.get("/waiting-room").then((r) => setRows(r.data)).catch(() => {});
  React.useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);
  const admit = async (w) => {
    try { const { data } = await api.post(`/waiting-room/${w.id}/admit`, { document_type: Object.keys(types)[0] || "Encounter Note" }); toast.success(`${w.name} admitted`); navigate(`/app/sessions/${data.id}/room`); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="rounded-[16px] border border-border bg-card p-5" data-testid="waiting-list">
      <p className="font-bold">Waiting room <span className="ml-1 rounded-full bg-secondary px-2 py-0.5 text-xs text-primary">{rows.length}</span></p>
      {rows.length === 0 ? <p className="mt-1 text-sm text-muted-foreground">No one is waiting. Clients enter their name and DOB on your room link and appear here. They never see each other.</p> : (
        <ul className="mt-3 divide-y divide-border">{rows.map((w) => <li key={w.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm" data-testid={`waiting-${w.id}`}>
          <div><p className="font-semibold">{w.name}</p><p className="text-xs text-muted-foreground">DOB {w.dob} · waiting since {new Date(w.since).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p></div>
          <div className="flex gap-2"><Button size="sm" onClick={() => admit(w)} data-testid={`admit-${w.id}`}><Video className="mr-1 h-4 w-4" /> Admit / start session</Button><Button size="sm" variant="ghost" onClick={() => api.post(`/waiting-room/${w.id}/dismiss`).then(load)}>Dismiss</Button></div></li>)}</ul>)}
    </div>
  );
}

function GroupRooms() {
  const navigate = useNavigate();
  const [rows, setRows] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [f, setF] = React.useState({ title: "", scheduled_at: "" });
  const load = () => api.get("/group-rooms").then((r) => setRows(r.data)).catch(() => {});
  React.useEffect(() => { load(); }, []);
  const create = async () => {
    try { const { data } = await api.post("/group-rooms", { title: f.title, scheduled_at: f.scheduled_at || null }); setOpen(false); navigate(`/app/group-rooms/${data.id}`); }
    catch (e) { toast.error(apiError(e)); }
  };
  const active = rows.filter((r) => r.status !== "ended");
  return (
    <div className="rounded-[16px] border border-border bg-card p-5" data-testid="group-rooms-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div><p className="font-bold">Group rooms <span className="ml-1 rounded-full bg-secondary px-2 py-0.5 text-xs text-primary">{active.length}</span></p>
          <p className="text-xs text-muted-foreground">Group therapy, family or collateral sessions — one shared link, you admit each person (up to 8 participants for now). Separate from your personal waiting line.</p></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button variant="outline" className="rounded-full" data-testid="new-group-room"><Plus className="mr-1 h-4 w-4" /> New group room</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create a group room</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Session name</Label><Input placeholder="e.g. Tuesday Anger Management Group, Family session — J. Smith" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="group-title" /></div>
              <div className="space-y-1.5"><Label>Scheduled for (optional)</Label><Input type="datetime-local" value={f.scheduled_at} onChange={(e) => setF({ ...f, scheduled_at: e.target.value })} data-testid="group-scheduled" /></div>
            </div>
            <DialogFooter><Button onClick={create} disabled={f.title.trim().length < 2} data-testid="group-create-submit"><Video className="mr-2 h-4 w-4" /> Create & open room</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {rows.length > 0 && <ul className="mt-3 divide-y divide-border">{rows.slice(0, 8).map((r) => <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm" data-testid={`group-row-${r.id}`}>
        <div><p className="font-semibold">{r.title}</p><p className="text-xs text-muted-foreground">{r.host_name} · <span className="capitalize">{r.status}</span>{r.scheduled_at ? ` · ${fmt(r.scheduled_at)}` : ""} · {r.counts.admitted} in · {r.counts.waiting} waiting</p></div>
        <Button size="sm" variant={r.status === "ended" ? "outline" : "default"} onClick={() => navigate(`/app/group-rooms/${r.id}`)} data-testid={`open-group-${r.id}`}>{r.status === "ended" ? "Note & attendance" : "Open room"}</Button>
      </li>)}</ul>}
    </div>
  );
}
